import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { Alert, Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { formatKpiValue, KPI_ACCENT, KPI_COLORS, pickLayout, rowValue } from '../kpiUtils';

const buildSeries = (rows, config) => {
    const labelKey = config.labelKey || 'label';
    const valueKey = config.valueKey || 'valor';
    const seriesKey = config.seriesKey || 'serie';
    const labels = [];
    const hasSeries = rows.some((row) => rowValue(row, seriesKey) !== null);

    if (!hasSeries) {
        const data = rows.map((row) => {
            const label = String(rowValue(row, labelKey, ['nome', 'titulo', 'label']) ?? '');
            if (!labels.includes(label)) {
                labels.push(label);
            }
            return Number(rowValue(row, valueKey, ['valor', 'value', 'total']) || 0);
        });
        return { labels, series: [{ name: config.seriesName || 'Valor', data }] };
    }

    const seriesNames = [];
    rows.forEach((row) => {
        const label = String(rowValue(row, labelKey, ['nome', 'titulo', 'label']) ?? '');
        const serie = String(rowValue(row, seriesKey, ['serie']) ?? 'Série');
        if (!labels.includes(label)) labels.push(label);
        if (!seriesNames.includes(serie)) seriesNames.push(serie);
    });

    const series = seriesNames.map((name) => ({
        name,
        data: labels.map((label) => {
            const match = rows.find(
                (row) =>
                    String(rowValue(row, labelKey, ['nome', 'titulo', 'label']) ?? '') === label &&
                    String(rowValue(row, seriesKey, ['serie']) ?? 'Série') === name
            );
            const raw = rowValue(match, valueKey, ['valor', 'value', 'total']);
            if (raw === null || raw === undefined || raw === '') {
                return null;
            }
            const number = Number(raw);
            return Number.isNaN(number) ? null : number;
        })
    }));

    return { labels, series };
};

const KpiChartCard = ({ item }) => {
    const tipo = (item.tipo || 'bar').toLowerCase();
    const layout = pickLayout(item.layout, { md: tipo === 'pie' || tipo === 'donut' || tipo === 'gauge' ? 4 : 8, height: 360 });
    const config = item.config || {};
    const rows = item.rows || [];

    if (item.error) {
        return (
            <Grid item xs={layout.xs} md={layout.md}>
                <Alert severity="warning">{item.error}</Alert>
            </Grid>
        );
    }

    const chart = buildSeries(rows, config);
    const colors = config.colors || KPI_COLORS;
    const baseOptions = {
        chart: { toolbar: { show: false }, fontFamily: 'inherit', foreColor: '#6B5A78' },
        colors,
        dataLabels: { enabled: tipo === 'pie' || tipo === 'donut' },
        legend: { position: 'bottom' },
        tooltip: { theme: 'light' },
        grid: { borderColor: '#F0E7F7' },
        stroke: { curve: 'smooth', width: tipo === 'line' || tipo === 'area' ? 3 : 0 }
    };

    let type = 'bar';
    let series = chart.series;
    let options = {
        ...baseOptions,
        plotOptions: {
            bar: {
                borderRadius: 6,
                columnWidth: '45%',
                horizontal: Boolean(config.horizontal),
                distributed: !chart.series.some((itemSeries) => itemSeries.name && chart.series.length > 1) && tipo === 'bar'
            }
        },
        xaxis: { categories: chart.labels },
        yaxis: {
            min: config.yaxisMin !== undefined ? Number(config.yaxisMin) : undefined,
            max: config.yaxisMax !== undefined ? Number(config.yaxisMax) : undefined,
            labels: {
                formatter: (value) => formatKpiValue(value, config.format || 'number', config)
            }
        }
    };

    if (config.meta !== undefined && config.meta !== null && config.meta !== '') {
        options.annotations = {
            yaxis: [
                {
                    y: Number(config.meta),
                    borderColor: config.metaColor || '#C62828',
                    strokeDashArray: 6,
                    label: {
                        text: config.metaLabel || `Meta ${config.meta}%`,
                        style: { color: '#fff', background: config.metaColor || '#C62828' }
                    }
                }
            ]
        };
    }

    if (tipo === 'line') type = 'line';
    if (tipo === 'area') {
        type = 'area';
        options.fill = { type: 'gradient', gradient: { opacityFrom: 0.45, opacityTo: 0.05 } };
    }
    if (tipo === 'pie' || tipo === 'donut') {
        type = tipo;
        series = chart.series[0]?.data || [];
        options = {
            ...baseOptions,
            labels: chart.labels,
            stroke: { width: 0 }
        };
    }
    if (tipo === 'gauge') {
        const row = rows[0] || {};
        const value = Number(rowValue(row, config.valueKey, ['valor', 'value']) || 0);
        const meta = Number(rowValue(row, config.metaKey, ['meta', 'total']) || 0);
        const percent = config.format === 'percent' && meta <= 0 ? value : meta > 0 ? (value / meta) * 100 : value;
        type = 'radialBar';
        series = [Math.max(0, Math.min(100, Number(percent) || 0))];
        options = {
            ...baseOptions,
            plotOptions: {
                radialBar: {
                    startAngle: -110,
                    endAngle: 110,
                    hollow: { size: '68%' },
                    track: { background: '#F0E7F7', strokeWidth: '100%' },
                    dataLabels: {
                        name: { show: false },
                        value: {
                            show: true,
                            fontSize: '22px',
                            fontWeight: 800,
                            color: '#2A1638',
                            offsetY: 6,
                            formatter: () =>
                                config.format === 'percent'
                                    ? formatKpiValue(percent, 'percent')
                                    : formatKpiValue(value, config.format || 'number', config)
                        }
                    }
                }
            },
            labels: [item.titulo || 'Meta'],
            colors: [config.color || KPI_ACCENT]
        };
    }

    return (
        <Grid item xs={layout.xs} sm={layout.sm} md={layout.md} lg={layout.lg}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #EDE4F5', height: '100%' }}>
                <CardContent>
                    {item.titulo && (
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2A1638', mb: 1 }}>
                            {item.titulo}
                        </Typography>
                    )}
                    <Box sx={{ minHeight: tipo === 'gauge' ? 150 : layout.height - 80 }}>
                        {rows.length === 0 && tipo !== 'gauge' ? (
                            <Box sx={{ py: 8, textAlign: 'center', color: '#8A7A98' }}>Sem dados para o filtro selecionado.</Box>
                        ) : (
                            <Chart
                                options={options}
                                series={series}
                                type={type}
                                height={tipo === 'gauge' ? 170 : layout.height - 70}
                            />
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );
};

KpiChartCard.propTypes = {
    item: PropTypes.object
};

export default KpiChartCard;
